import * as compareStoreModule from "./sn-compare-store.js";
import {
  compareDebug,
  resolveVariantIdByImage,
  resolveVariantIdForCompareItem
} from "./sn-compare-variant.js";

const {
  snCompareStore,
  CHANGE_EVENT,
  MAX_ITEMS,
  toRootPath,
  sanitizeHandle,
  syncUrlFromStore,
  resolveComparePageEntries
} = compareStoreModule;

const getLastEligibleCompareCollectionUrl =
  compareStoreModule.getLastEligibleCompareCollectionUrl ||
  ((fallbackUrl = toRootPath("/collections/all")) => fallbackUrl);
const saveLastEligibleCompareCollectionUrl =
  compareStoreModule.saveLastEligibleCompareCollectionUrl ||
  (() => false);

const SPEC_SECTION_ID = "sn-section-compare-product-specs";
const MOBILE_COMPARE_MQ = "(max-width: 991px)";
const EXCLUDED_COLLECTION_HANDLES = new Set([
  "new-arrivals",
  "sale",
  "hottest-deals",
  "orderly-emails-recommended-products"
]);

const COMPARE_TYPO = {
  columnTitle: "hdt-h2 hdt-heading",
  productTitle: "hdt-link hdt-font-medium hdt-text-lg hdt-s-text",
  productPrice: "hdt-text-lg text-neutral-400",
  specLabel: "hdt-text-lg text-neutral-400 hdt-font-medium",
  specValue: "hdt-text-lg hdt-font-medium",
  mobileSpecLabel: "hdt-text-sm text-neutral-400 hdt-font-medium",
  mobileSpecValue: "hdt-text-sm hdt-font-medium",
  addProductLabel: "hdt-font-semibold hdt-text-sm"
};

function parseCollectionReferrerUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  const referrer = String(document.referrer || "").trim();
  if (!referrer) {
    return "";
  }

  try {
    const parsed = new URL(referrer, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return "";
    }

    const match = parsed.pathname.match(/^\/collections\/([^/]+)/);
    const collectionHandle = sanitizeHandle(match?.[1] || "");
    if (!collectionHandle || EXCLUDED_COLLECTION_HANDLES.has(collectionHandle)) {
      return "";
    }

    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return "";
  }
}

function resolveCompareCollectionReturnUrl(fallbackUrl) {
  const fallback = String(fallbackUrl || "").trim() || toRootPath("/collections/all");
  const fromSession = getLastEligibleCompareCollectionUrl(fallback);
  const fromReferrer = parseCollectionReferrerUrl();

  if (fromReferrer) {
    const handleMatch = fromReferrer.match(/^\/collections\/([^/?#]+)/);
    const collectionHandle = sanitizeHandle(handleMatch?.[1] || "");
    if (collectionHandle) {
      saveLastEligibleCompareCollectionUrl(collectionHandle, fromReferrer);
    }
    return fromReferrer;
  }

  return fromSession;
}

function appendVariantParam(url, variantId) {
  const baseUrl = String(url || "").trim();
  const id = String(variantId || "").trim();
  if (!baseUrl || !id) {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl, window.location.origin);
    parsed.searchParams.set("variant", id);
    return `${parsed.pathname}${parsed.search}`;
  } catch (error) {
    return baseUrl;
  }
}

function parseProductQueryPart(part) {
  const decoded = decodeURIComponent(String(part || "").trim());
  const separatorIndex = decoded.indexOf(":");

  if (separatorIndex === -1) {
    return {
      handle: sanitizeHandle(decoded),
      variantId: ""
    };
  }

  return {
    handle: sanitizeHandle(decoded.slice(0, separatorIndex)),
    variantId: String(decoded.slice(separatorIndex + 1)).trim()
  };
}

function parseProductsFromUrl(url = typeof window !== "undefined" ? window.location.href : "") {
  try {
    const params = new URL(url, window.location.origin).searchParams;
    const rawProducts = params.get("products");
    if (!rawProducts) {
      return [];
    }

    const products = [];
    const seen = new Set();

    rawProducts.split(",").forEach((part) => {
      const parsed = parseProductQueryPart(part);
      if (!parsed.handle || seen.has(parsed.handle)) {
        return;
      }

      seen.add(parsed.handle);
      products.push(parsed);
    });

    return products.slice(0, MAX_ITEMS);
  } catch (error) {
    return [];
  }
}

function getVariantIdsByHandleFromUrl(url = typeof window !== "undefined" ? window.location.href : "") {
  return Object.fromEntries(
    parseProductsFromUrl(url)
      .filter((product) => product.variantId)
      .map((product) => [product.handle, product.variantId])
  );
}

function getCompareVariantIdsByHandle(url = typeof window !== "undefined" ? window.location.href : "") {
  const fromUrl = getVariantIdsByHandleFromUrl(url);
  const fromStore = Object.fromEntries(
    snCompareStore
      .getItems()
      .filter((item) => item.variantId)
      .map((item) => [sanitizeHandle(item.handle), String(item.variantId)])
  );

  return { ...fromUrl, ...fromStore };
}

function getPersistedCompareSelections() {
  return new Map(
    snCompareStore.getItems().map((item) => [
      sanitizeHandle(item.handle),
      {
        variantId: String(item.variantId || "").trim(),
        image: String(item.image || "").trim(),
        url: String(item.url || "").trim()
      }
    ])
  );
}

function isMobileCompareLayout() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_COMPARE_MQ).matches;
}

const SKELETON_SPEC_ROW_COUNT = 3;
const LOADING_START = "hdt:loading:start";
const LOADING_END = "hdt:loading:end";

let compareAtcSpinnerMarkup = null;

function getCompareAtcSpinnerMarkup() {
  if (compareAtcSpinnerMarkup === null) {
    const template = document.getElementById("sn-compare-atc-spinner");
    compareAtcSpinnerMarkup =
      template?.innerHTML?.trim() ||
      `<div class="hdt-loading-area__spinner"><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="hdt-icon-spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="hdt-path"></circle></svg></div>`;
  }
  return compareAtcSpinnerMarkup;
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function formatPrice(cents) {
  if (!Number.isFinite(cents)) {
    return "-";
  }

  const currencyCode =
    typeof window !== "undefined" && window.Shopify?.currency?.active
      ? window.Shopify.currency.active
      : "AUD";
  const locale = document.documentElement.lang || "en-AU";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode
  }).format(cents / 100);
}

function getCompareVariant(product, variantId) {
  if (!product?.variants?.length) {
    return null;
  }

  if (variantId) {
    const match = product.variants.find((variant) => String(variant.id) === String(variantId));
    if (match) {
      return match;
    }
  }

  return product.variants.find((variant) => variant.available) || product.variants[0];
}

function normalizeImageUrl(image) {
  if (!image) {
    return "";
  }

  if (typeof image === "string") {
    return image.trim();
  }

  if (typeof image === "object" && typeof image.src === "string") {
    return image.src.trim();
  }

  return "";
}

function getProductFeaturedImage(product) {
  return normalizeImageUrl(product?.featured_image) || normalizeImageUrl(product?.images?.[0]) || "";
}

function getVariantImage(product, variant) {
  if (!variant) {
    return getProductFeaturedImage(product);
  }

  const variantFeatured = normalizeImageUrl(variant.featured_image);
  if (variantFeatured) {
    return variantFeatured;
  }

  if (Array.isArray(product?.images)) {
    for (const image of product.images) {
      const variantIds = Array.isArray(image?.variant_ids) ? image.variant_ids : [];
      if (variantIds.some((id) => String(id) === String(variant.id))) {
        const linkedImage = normalizeImageUrl(image.src || image);
        if (linkedImage) {
          return linkedImage;
        }
      }
    }
  }

  return "";
}

function resolveCompareDisplay(product, variantId, storedDisplay = {}) {
  const variant = getCompareVariant(product, variantId);
  const storedImage = normalizeImageUrl(storedDisplay.image);
  const image =
    getVariantImage(product, variant) ||
    storedImage ||
    getProductFeaturedImage(product);
  const price = variant?.price ?? product.price;
  const compareAtPrice = variant?.compare_at_price ?? product.compare_at_price;
  const hasCompareAtPrice = Number.isFinite(compareAtPrice) && compareAtPrice > price;
  const url =
    (variant?.id ? appendVariantParam(product.url.split("?")[0], variant.id) : "") ||
    storedDisplay.url ||
    product.url;

  return {
    variant,
    image,
    price,
    compareAtPrice: hasCompareAtPrice ? compareAtPrice : null,
    url
  };
}

function getEntryCompareDisplay(entry) {
  if (!entry?.product) {
    return { variant: null, image: "", price: 0, compareAtPrice: null, url: "" };
  }

  return resolveCompareDisplay(entry.product, entry.variantId, {
    image: entry.compareImage,
    url: entry.compareUrl
  });
}

function resolveVariantImageFromProduct(product, variantId) {
  const variant = getCompareVariant(product, variantId);
  return getVariantImage(product, variant);
}

function resolveVariantIdFromSwatchRadio(radio, product) {
  const { vid } = radio.dataset;
  if (vid) {
    return String(vid);
  }

  const optionValue = String(radio.value || "").trim();
  if (!optionValue || !product?.variants?.length) {
    return "";
  }

  const matches = product.variants.filter(
    (variant) =>
      variant.option1 === optionValue ||
      variant.option2 === optionValue ||
      variant.option3 === optionValue
  );

  const match = matches.find((variant) => variant.available) || matches[0];
  return match?.id != null ? String(match.id) : "";
}

function resolveVariantImageFromSwatchRadio(radio, product, variantId) {
  const { vimg } = radio.dataset;
  if (vimg) {
    return vimg;
  }

  return resolveVariantImageFromProduct(product, variantId);
}

function attachVariantIdsToEntries(entries, variantIdsByHandle = {}, persistedSelections = null) {
  const storeItemsByHandle = new Map(snCompareStore.getItems().map((item) => [item.handle, item]));
  const persistedByHandle =
    persistedSelections instanceof Map ? persistedSelections : getPersistedCompareSelections();

  return (entries || []).map((entry) => {
    const handle = sanitizeHandle(entry?.product?.handle);
    const storeItem = storeItemsByHandle.get(handle);
    const persisted = persistedByHandle.get(handle);
    let variantId =
      persisted?.variantId ||
      variantIdsByHandle[handle] ||
      storeItem?.variantId ||
      entry?.variantId ||
      "";

    if (!variantId && persisted?.image && entry?.product) {
      variantId = resolveVariantIdByImage(entry.product, persisted.image);
      compareDebug("attach:resolved-from-persisted-image", { handle, variantId, image: persisted.image });
    }

    if (!variantId && entry?.product) {
      const resolved = resolveVariantIdForCompareItem(
        {
          handle,
          variantId: "",
          image: persisted?.image || storeItem?.image || entry.compareImage,
          url: persisted?.url || storeItem?.url || entry.compareUrl
        },
        entry.product
      );
      if (resolved) {
        variantId = resolved;
        compareDebug("attach:resolved-from-product", { handle, variantId });
      }
    }
    const persistedMediaMatches =
      Boolean(persisted?.variantId) && String(persisted.variantId) === String(variantId);
    const useStoreMedia =
      Boolean(storeItem?.variantId) &&
      String(storeItem.variantId) === String(variantId) &&
      Boolean(storeItem?.image);
    const compareImage = persistedMediaMatches && persisted.image
      ? persisted.image
      : useStoreMedia
        ? storeItem.image
        : resolveVariantImageFromProduct(entry.product, variantId);
    const compareUrl = persistedMediaMatches && persisted.url
      ? persisted.url
      : useStoreMedia && storeItem?.url
        ? storeItem.url
        : variantId
          ? appendVariantParam(entry.product.url.split("?")[0], variantId)
          : entry.product.url;

    return {
      ...entry,
      variantId,
      compareImage,
      compareUrl
    };
  });
}

function sanitizeCompareSwatchHtml(html) {
  if (!html) {
    return "";
  }

  return html
    .replace(/<\s*wrapp-hdt-swatches\b([^>]*)>/gi, '<div class="s-compare-page__swatch-host"$1>')
    .replace(/<\s*\/\s*wrapp-hdt-swatches\s*>/gi, "</div>");
}

function updateCompareStoreItem(handle, updates = {}) {
  if (typeof snCompareStore.updateItem === "function") {
    return snCompareStore.updateItem(handle, updates);
  }

  const normalized = sanitizeHandle(handle);
  if (!normalized) {
    return false;
  }

  const items = snCompareStore.getItems();
  const index = items.findIndex((item) => item.handle === normalized);
  if (index === -1) {
    return false;
  }

  const current = items[index];
  const variantId =
    updates.variantId != null ? String(updates.variantId).trim() : current.variantId;
  const baseUrl = (updates.url || current.url || toRootPath(`/products/${normalized}`)).split("?")[0];
  const nextItem = {
    ...current,
    ...updates,
    handle: normalized,
    variantId,
    url: variantId ? appendVariantParam(baseUrl, variantId) : baseUrl,
    image: updates.image != null ? String(updates.image).trim() : current.image
  };

  if (
    current.variantId === nextItem.variantId &&
    current.image === nextItem.image &&
    current.url === nextItem.url &&
    current.title === nextItem.title
  ) {
    return false;
  }

  const nextItems = [...items.slice(0, index), nextItem, ...items.slice(index + 1)];
  return snCompareStore.setItems(nextItems);
}

async function fetchProduct(handle) {
  const productUrl = toRootPath(`/products/${handle}.js`);
  const response = await fetch(productUrl, { credentials: "same-origin" });
  if (!response.ok) {
    return null;
  }
  const product = await response.json();
  if (!product?.handle) {
    return null;
  }
  return product;
}

async function fetchProductSpecs(handle, variantId = "") {
  const params = new URLSearchParams({ sections: SPEC_SECTION_ID });
  if (variantId) {
    params.set("variant", String(variantId));
  }

  const url = toRootPath(`/products/${handle}?${params.toString()}`);
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    return { specifications: {}, warranty: null, collections: [], swatchHtml: "" };
  }

  const payload = await response.json();
  const html = payload?.[SPEC_SECTION_ID];
  if (!html) {
    return { specifications: {}, warranty: null, collections: [], swatchHtml: "" };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const node = doc.querySelector("[data-compare-specs]");
  const swatchNode = doc.querySelector("[data-compare-swatches]");
  const swatchHtml = sanitizeCompareSwatchHtml(swatchNode?.innerHTML?.trim() || "");

  if (!node?.textContent) {
    return { specifications: {}, warranty: null, collections: [], swatchHtml };
  }

  try {
    const parsed = JSON.parse(node.textContent);
    return {
      specifications: parsed?.specifications && typeof parsed.specifications === "object" ? parsed.specifications : {},
      warranty: parsed?.warranty ?? null,
      collections: Array.isArray(parsed?.collections) ? parsed.collections : [],
      swatchHtml
    };
  } catch (error) {
    return { specifications: {}, warranty: null, collections: [], swatchHtml };
  }
}

function getVariantIdForHandle(handle, variantIdsByHandle = {}) {
  const normalized = sanitizeHandle(handle);
  return variantIdsByHandle[normalized] || "";
}

function syncCompareSwatchSelection(gridNode, entry, colIndex) {
  if (!gridNode || !entry?.variantId) {
    return;
  }

  const variantId = String(entry.variantId);
  const radios = gridNode.querySelectorAll(
    `[data-compare-col="${colIndex}"] .s-compare-page__price-swatch [type="radio"]`
  );

  if (radios.length === 0) {
    return;
  }

  const variant = entry.product ? getCompareVariant(entry.product, variantId) : null;
  let targetRadio = null;

  radios.forEach((radio) => {
    if (String(radio.dataset.vid || "") === variantId) {
      targetRadio = radio;
    }
  });

  if (!targetRadio && variant) {
    const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    radios.forEach((radio) => {
      if (!targetRadio && optionValues.includes(radio.value)) {
        targetRadio = radio;
      }
    });
  }

  if (!targetRadio) {
    return;
  }

  radios.forEach((radio) => {
    radio.checked = radio === targetRadio;
  });
}

function syncCompareSwatchSelections(gridNode, entries) {
  (entries || []).forEach((entry, index) => {
    syncCompareSwatchSelection(gridNode, entry, index);
  });
}

function buildSpecRows(products, specRowOrder, warrantyLabel) {
  const preferred = String(specRowOrder || "")
    .split("|")
    .map((key) => key.trim())
    .filter(Boolean);
  const keySet = new Set();

  products.forEach((entry) => {
    Object.keys(entry.specs?.specifications || {}).forEach((key) => keySet.add(key));
    if (entry.specs?.warranty) {
      keySet.add(warrantyLabel);
    }
  });

  const rows = [];
  preferred.forEach((key) => {
    if (keySet.has(key)) {
      rows.push(key);
      keySet.delete(key);
    }
  });

  [...keySet].sort((a, b) => a.localeCompare(b)).forEach((key) => rows.push(key));
  return rows;
}

function getSpecValue(entry, rowKey, warrantyLabel) {
  if (rowKey === warrantyLabel) {
    const years = entry.specs?.warranty;
    return years ? `${years} Years` : "-";
  }
  const value = entry.specs?.specifications?.[rowKey];
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    return "-";
  }
  return String(value);
}

const ALLOWED_SPEC_TAGS = new Set(["BR", "SPAN", "EM", "STRONG", "SUP", "SUB", "SMALL", "B", "I"]);
const ALLOWED_SPEC_ATTRS = {
  SPAN: new Set(["style", "class"])
};

function isUnsafeSpecStyle(style) {
  return /expression|javascript:|@import|url\s*\(/i.test(style || "");
}

function sanitizeSpecHtml(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstElementChild;
  if (!container) {
    return "";
  }

  const sanitizeNode = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        return;
      }

      const tag = child.tagName;
      if (!ALLOWED_SPEC_TAGS.has(tag)) {
        while (child.firstChild) {
          node.insertBefore(child.firstChild, child);
        }
        child.remove();
        return;
      }

      [...child.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on")) {
          child.removeAttribute(attr.name);
          return;
        }

        const allowed = ALLOWED_SPEC_ATTRS[tag];
        if (!allowed?.has(name)) {
          child.removeAttribute(attr.name);
        }
      });

      if (child.hasAttribute("style") && isUnsafeSpecStyle(child.getAttribute("style"))) {
        child.removeAttribute("style");
      }

      sanitizeNode(child);
    });
  };

  sanitizeNode(container);
  return container.innerHTML;
}

function formatSpecValueHtml(value) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }

  const raw = String(value).trim();
  if (!/[<>]/.test(raw)) {
    return escapeHtml(raw);
  }

  const sanitized = sanitizeSpecHtml(raw);
  return sanitized || "-";
}

function renderRule() {
  return '<hr class="s-compare-page__rule" aria-hidden="true">';
}

function getSkeletonSpecRowKeys(specRowOrder, count = SKELETON_SPEC_ROW_COUNT) {
  const preferred = String(specRowOrder || "")
    .split("|")
    .map((key) => key.trim())
    .filter(Boolean);

  if (preferred.length >= count) {
    return preferred.slice(0, count);
  }

  const fallback = ["Settings", "Weight", "Accessories"];
  return [...preferred, ...fallback].slice(0, count);
}

function renderSkeletonSurface(className) {
  return `<div class="sn-compare-page__skeleton-surface ${className}" shimmer aria-hidden="true"></div>`;
}

function renderSkeletonTitle() {
  return `
    <div class="sn-compare-page__skeleton-lines" aria-hidden="true">
      ${renderSkeletonSurface("sn-compare-page__skeleton-surface--title-line")}
      ${renderSkeletonSurface("sn-compare-page__skeleton-surface--title-line sn-compare-page__skeleton-surface--title-line-short")}
    </div>
  `;
}

function renderSkeletonPrice() {
  return `
    <div class="sn-compare-page__skeleton-price" aria-hidden="true">
      ${renderSkeletonSurface("sn-compare-page__skeleton-surface--price")}
    </div>
  `;
}

function renderSkeletonProductImageCell() {
  return `
    <div class="s-compare-page__product-media">
      <div class="s-compare-page__product-image-wrap">
        ${renderSkeletonSurface("sn-compare-page__skeleton-surface--image")}
      </div>
    </div>
  `;
}

function renderSkeletonProductActionsCell() {
  return `
    <div class="s-compare-page__product-actions" aria-hidden="true">
      ${renderSkeletonSurface("sn-compare-page__skeleton-surface--btn-primary")}
      ${renderSkeletonSurface("sn-compare-page__skeleton-surface--btn-secondary")}
    </div>
  `;
}

function renderSkeletonHeaderRows(pageTitle, labels, specRowOrder) {
  const titleCell = `
    <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--title">
      <h2 class="s-compare-page__column-title ${COMPARE_TYPO.columnTitle}">${escapeHtml(pageTitle)}</h2>
    </div>
  `;

  const productCells = (renderCell) =>
    Array.from({ length: MAX_ITEMS }, (_, index) => {
      return `
        <div class="s-compare-page__cell ${renderCell.className}" data-compare-col="${index}" data-skeleton-col>
          ${renderCell.content}
        </div>
      `;
    }).join("");

  const imageCells = productCells({
    className: "s-compare-page__cell--header-image is-skeleton",
    content: renderSkeletonProductImageCell()
  });

  const titleCells = productCells({
    className: "s-compare-page__cell--header-title is-skeleton",
    content: renderSkeletonTitle()
  });

  const priceCells = productCells({
    className: "s-compare-page__cell--header-price is-skeleton",
    content: renderSkeletonPrice()
  });

  const actionCells = productCells({
    className: "s-compare-page__cell--header-actions is-skeleton",
    content: renderSkeletonProductActionsCell()
  });

  return (
    titleCell +
    imageCells +
    renderHeaderLabelSpacer() +
    titleCells +
    renderHeaderLabelSpacer() +
    priceCells +
    renderHeaderLabelSpacer() +
    actionCells
  );
}

function renderSkeletonSpecRows(specRowOrder) {
  const rowKeys = getSkeletonSpecRowKeys(specRowOrder);

  return rowKeys
    .map(() => {
      const labelCell = `
        <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--spec is-skeleton">
          ${renderRule()}
          <div class="s-compare-page__spec-label">${renderSkeletonSurface("sn-compare-page__skeleton-surface--spec-label")}</div>
        </div>
      `;

      const valueCells = Array.from({ length: MAX_ITEMS }, (_, index) => {
        return `
          <div class="s-compare-page__cell s-compare-page__cell--spec is-skeleton" data-compare-col="${index}" data-skeleton-col>
            ${renderRule()}
            <div class="s-compare-page__spec-value">${renderSkeletonSurface("sn-compare-page__skeleton-surface--spec-value")}</div>
          </div>
        `;
      }).join("");

      return labelCell + valueCells;
    })
    .join("");
}

function renderSkeletonFooterRows() {
  return Array.from({ length: MAX_ITEMS }, (_, index) => {
    return `
      <div class="s-compare-page__cell s-compare-page__cell--footer is-skeleton" data-compare-col="${index}" data-skeleton-col>
        ${renderSkeletonSurface("sn-compare-page__skeleton-surface--btn-primary sn-compare-page__skeleton-surface--block")}
      </div>
    `;
  }).join("");
}

function renderSkeletonTable(pageTitle, labels, specRowOrder) {
  if (isMobileCompareLayout()) {
    return renderMobileSkeletonTable(labels, specRowOrder);
  }

  return `
    <div class="s-compare-page__table s-compare-page__table--skeleton" data-compare-skeleton>
      ${renderSkeletonHeaderRows(pageTitle, labels, specRowOrder)}
      ${renderSkeletonSpecRows(specRowOrder)}
      <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--footer-spacer" aria-hidden="true"></div>
      ${renderSkeletonFooterRows()}
    </div>
  `;
}

function renderMobileSpecValueCell(entry, rowKey, warrantyLabel, isSkeleton = false) {
  const value = entry ? getSpecValue(entry, rowKey, warrantyLabel) : "-";
  const valueHtml = entry
    ? formatSpecValueHtml(value)
    : renderSkeletonSurface("sn-compare-page__skeleton-surface--spec-value");
  const cellLabelHtml = isSkeleton
    ? renderSkeletonSurface("sn-compare-page__skeleton-surface--spec-label")
    : `<span class="s-compare-page__spec-label s-compare-page__mobile-spec-cell-label ${COMPARE_TYPO.mobileSpecLabel}">${escapeHtml(rowKey)}</span>`;

  return `
    ${cellLabelHtml}
    <div class="s-compare-page__spec-value ${COMPARE_TYPO.mobileSpecValue}">${valueHtml}</div>
  `;
}

function renderMobileSpecRow(rowKey, rowIndex, activeSlots, warrantyLabel, isSkeleton = false) {
  const showRule = rowIndex > 0;
  const labelHtml = isSkeleton
    ? renderSkeletonSurface("sn-compare-page__skeleton-surface--spec-label")
    : `<span class="s-compare-page__spec-label ${COMPARE_TYPO.mobileSpecLabel}">${escapeHtml(rowKey)}</span>`;

  const valueCells = activeSlots
    .map((slot, index) => {
      const content = renderMobileSpecValueCell(slot.entry, rowKey, warrantyLabel, isSkeleton);
      return renderMobileCell(slot, index, "spec", content, isSkeleton);
    })
    .join("");

  return `
    <div class="s-compare-page__mobile-spec-row${isSkeleton ? " is-skeleton" : ""}">
      ${showRule ? renderRule() : ""}
      <div class="s-compare-page__mobile-spec-label">${labelHtml}</div>
      <div class="s-compare-page__mobile-spec-values">${valueCells}</div>
    </div>
  `;
}

function renderMobileSpecSection(activeSlots, specRows, warrantyLabel, isSkeleton = false) {
  const rowsHtml = specRows
    .map((rowKey, rowIndex) => renderMobileSpecRow(rowKey, rowIndex, activeSlots, warrantyLabel, isSkeleton))
    .join("");

  return `<div class="s-compare-page__mobile-spec-section">${rowsHtml}</div>`;
}

function renderMobileCell(slot, index, modifier, content, isSkeleton = false) {
  const isEmpty = !isSkeleton && !slot?.entry?.product;
  return `
    <div
      class="s-compare-page__mobile-cell s-compare-page__mobile-cell--${modifier}${isEmpty ? " is-empty" : ""}${isSkeleton ? " is-skeleton" : ""}"
      data-compare-col="${index}"
      ${slot?.entry?.product ? `data-handle="${escapeHtml(slot.entry.product.handle)}"` : ""}
      ${isSkeleton ? "data-skeleton-col" : ""}
    >
      ${content}
    </div>
  `;
}

function renderMobileGridRows(slots, specRows, labels, closeIcon, shopAllUrl, warrantyLabel, isSkeleton = false) {
  const activeSlots = slots.slice(0, MAX_ITEMS);

  const imageRow = activeSlots
    .map((slot, index) => {
      const content = isSkeleton
        ? renderSkeletonProductImageCell()
        : slot.entry?.product
          ? renderProductImageCell(slot.entry, closeIcon, labels)
          : renderEmptyProductCell(shopAllUrl, labels);
      return renderMobileCell(slot, index, "image", content, isSkeleton);
    })
    .join("");

  const titleRow = activeSlots
    .map((slot, index) => {
      const content = isSkeleton
        ? renderSkeletonTitle()
        : slot.entry?.product
          ? renderProductTitleCell(slot.entry)
          : "";
      return renderMobileCell(slot, index, "title", content, isSkeleton);
    })
    .join("");

  const priceRow = activeSlots
    .map((slot, index) => {
      const content = isSkeleton
        ? renderSkeletonPrice()
        : slot.entry?.product
          ? renderProductPriceCell(slot.entry)
          : "";
      return renderMobileCell(slot, index, "price", content, isSkeleton);
    })
    .join("");

  const actionsRow = activeSlots
    .map((slot, index) => {
      const content = isSkeleton
        ? renderSkeletonProductActionsCell()
        : slot.entry?.product
          ? renderProductActionsCell(slot.entry, labels)
          : "";
      return renderMobileCell(slot, index, "actions", content, isSkeleton);
    })
    .join("");

  const specSectionHtml = renderMobileSpecSection(activeSlots, specRows, warrantyLabel, isSkeleton);

  const footerRow = activeSlots
    .map((slot, index) => {
      const content = isSkeleton
        ? renderSkeletonSurface("sn-compare-page__skeleton-surface--btn-primary sn-compare-page__skeleton-surface--block")
        : slot.entry?.product
          ? renderAtcButton(getEntryCompareDisplay(slot.entry).variant, labels, "s-compare-page__btn--block")
          : "";
      return renderMobileCell(slot, index, "footer", content, isSkeleton);
    })
    .join("");

  return `
    ${imageRow}
    ${titleRow}
    ${priceRow}
    <div class="s-compare-page__mobile-divider" aria-hidden="true"></div>
    ${actionsRow}
    <div class="s-compare-page__mobile-divider" aria-hidden="true"></div>
    ${specSectionHtml}
    <div class="s-compare-page__mobile-divider" aria-hidden="true"></div>
    ${footerRow}
  `;
}

function renderMobileGridWrapper(colCount, rowsHtml, isSkeleton = false) {
  return `
    <div class="s-compare-page__table s-compare-page__table--mobile${isSkeleton ? " s-compare-page__table--skeleton" : ""}"${isSkeleton ? " data-compare-skeleton" : ""}>
      <div class="s-compare-page__mobile-scroll">
        <div
          class="s-compare-page__mobile-grid${isSkeleton ? " is-skeleton" : ""}"
          style="--compare-mobile-active-cols: ${colCount}"
        >
          ${rowsHtml}
        </div>
      </div>
    </div>
  `;
}

function renderMobileSkeletonTable(labels, specRowOrder) {
  const rowKeys = getSkeletonSpecRowKeys(specRowOrder);
  const slots = Array.from({ length: MAX_ITEMS }, (_, index) => ({
    entry: null,
    index
  }));

  return renderMobileGridWrapper(
    MAX_ITEMS,
    renderMobileGridRows(slots, rowKeys, labels, "", "", "", true),
    true
  );
}

function renderMobileGrid(slots, specRows, labels, closeIcon, shopAllUrl, warrantyLabel) {
  return renderMobileGridWrapper(
    MAX_ITEMS,
    renderMobileGridRows(slots, specRows, labels, closeIcon, shopAllUrl, warrantyLabel)
  );
}

function renderAtcButton(variant, labels, extraClass = "") {
  const unavailable = variant?.available === false;
  const buttonLabel = unavailable ? labels.soldOut : labels.addToCart;
  const disabledAttr = unavailable ? " disabled" : "";
  const className = [
    "s-compare-page__btn",
    "s-compare-page__btn--primary",
    "hdt-product-form__submit",
    "hdt-btn",
    "hdt-btn-solid",
    "hdt-btn--size-s",
    unavailable ? "s-compare-page__btn--sold-out" : "hdt-btn--loading_spinner",
    extraClass
  ]
    .filter(Boolean)
    .join(" ");

  const spinnerMarkup = unavailable ? "" : getCompareAtcSpinnerMarkup();

  return `<button circle-bg-wrap type="button" aria-label="${escapeHtml(buttonLabel)}" class="${className}" data-compare-add-to-cart data-variant-id="${variant?.id != null ? String(variant.id) : ""}"${disabledAttr}>
    <span>${escapeHtml(buttonLabel)}</span>
    ${spinnerMarkup}
    <hdt-circle-bg mousemove></hdt-circle-bg>
  </button>`;
}

function setCompareAtcLoading(button, isLoading) {
  if (!button) {
    return;
  }

  if (isLoading) {
    button.setAttribute("aria-disabled", "true");
    button.setAttribute("aria-busy", "true");
    document.dispatchEvent(new CustomEvent(LOADING_START));
    return;
  }

  button.removeAttribute("aria-disabled");
  button.removeAttribute("aria-busy");
  document.dispatchEvent(new CustomEvent(LOADING_END));
}

function renderHeaderLabelSpacer() {
  return `<div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--header-spacer" aria-hidden="true"></div>`;
}

function renderProductImageCell(entry, closeIcon, labels) {
  const product = entry.product;
  const { image: imageSrc, url } = getEntryCompareDisplay(entry);
  const removeLabel = labels.removeTemplate.replace("[title]", product.title);

  return `
    <div class="s-compare-page__product-media">
      <button type="button" class="s-compare-page__product-remove" data-compare-remove data-handle="${escapeHtml(product.handle)}" aria-label="${escapeHtml(removeLabel)}">
        ${closeIcon}
      </button>
      <a class="s-compare-page__product-image-link" href="${escapeHtml(url)}">
        <span class="s-compare-page__product-image-wrap">
          ${imageSrc ? `<img class="s-compare-page__product-image" src="${escapeHtml(imageSrc)}" alt="${escapeHtml(product.title)}" loading="lazy" width="318" height="318">` : ""}
        </span>
      </a>
    </div>
  `;
}

function renderProductTitleCell(entry) {
  const { url } = getEntryCompareDisplay(entry);
  return `<a class="s-compare-page__product-title ${COMPARE_TYPO.productTitle}" href="${escapeHtml(url)}">${escapeHtml(entry.product.title)}</a>`;
}

function renderProductPriceMarkup(entry) {
  const { price, compareAtPrice } = getEntryCompareDisplay(entry);
  const compareAtHtml = compareAtPrice
    ? `<hdt-compare-at-price class="hdt-compare-at-price"><span class="hdt-money">${formatPrice(compareAtPrice)}</span></hdt-compare-at-price>`
    : "";

  return `<div class="hdt-price-wrapp"><div class="hdt-price__list">${compareAtHtml}<hdt-price class="hdt-price s-compare-page__product-price ${COMPARE_TYPO.productPrice}"><span class="hdt-money">${formatPrice(price)}</span></hdt-price></div></div>`;
}

function renderProductPriceCell(entry) {
  const swatchHtml = entry.swatchHtml || "";
  const priceMarkup = renderProductPriceMarkup(entry);

  if (!swatchHtml) {
    return priceMarkup;
  }

  return `
    <div class="sn-price-swatch-wrapper s-compare-page__price-swatch">
      ${priceMarkup}
      ${swatchHtml}
    </div>
  `;
}

function renderProductActionsCell(entry, labels) {
  const { variant, url } = getEntryCompareDisplay(entry);
  return `
    <div class="s-compare-page__product-actions">
      ${renderAtcButton(variant, labels)}
      <a circle-bg-wrap class="s-compare-page__btn s-compare-page__btn--secondary hdt-btn hdt-btn-outline hdt-btn--size-s" href="${escapeHtml(url)}">
        <span>${escapeHtml(labels.learnMore)}</span>
        <hdt-circle-bg mousemove></hdt-circle-bg>
      </a>
    </div>
  `;
}

function renderEmptyProductCell(shopAllUrl, labels) {
  return `
    <div class="s-compare-page__product-image-wrap">
      <a class="s-compare-page__add-product" href="${escapeHtml(shopAllUrl)}">
        <span class="s-compare-page__add-product-label ${COMPARE_TYPO.addProductLabel}">${escapeHtml(labels.addProduct)}</span>
      </a>
    </div>
  `;
}

function renderHeaderRows(slots, pageTitle, labels, closeIcon, shopAllUrl, plusIcon) {
  const titleCell = `
    <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--title">
      <h2 class="s-compare-page__column-title ${COMPARE_TYPO.columnTitle}">${escapeHtml(pageTitle)}</h2>
    </div>
  `;

  const imageCells = slots
    .map(({ entry, index }) => {
      if (!entry?.product) {
        return `
          <div class="s-compare-page__cell s-compare-page__cell--header-image is-empty" data-compare-col="${index}">
            ${renderEmptyProductCell(shopAllUrl, labels)}
          </div>
        `;
      }
      const product = entry.product;
      return `
        <div class="s-compare-page__cell s-compare-page__cell--header-image" data-compare-col="${index}" data-handle="${escapeHtml(product.handle)}">
          ${renderProductImageCell(entry, closeIcon, labels)}
        </div>
      `;
    })
    .join("");

  const titleCells = slots
    .map(({ entry, index }) => {
      if (!entry?.product) {
        return `<div class="s-compare-page__cell s-compare-page__cell--header-title is-empty" data-compare-col="${index}"></div>`;
      }
      return `
        <div class="s-compare-page__cell s-compare-page__cell--header-title" data-compare-col="${index}" data-handle="${escapeHtml(entry.product.handle)}">
          ${renderProductTitleCell(entry)}
        </div>
      `;
    })
    .join("");

  const priceCells = slots
    .map(({ entry, index }) => {
      if (!entry?.product) {
        return `<div class="s-compare-page__cell s-compare-page__cell--header-price is-empty" data-compare-col="${index}"></div>`;
      }
      return `
        <div class="s-compare-page__cell s-compare-page__cell--header-price" data-compare-col="${index}" data-handle="${escapeHtml(entry.product.handle)}">
          ${renderProductPriceCell(entry)}
        </div>
      `;
    })
    .join("");

  const actionCells = slots
    .map(({ entry, index }) => {
      if (!entry?.product) {
        return `<div class="s-compare-page__cell s-compare-page__cell--header-actions is-empty" data-compare-col="${index}"></div>`;
      }
      return `
        <div class="s-compare-page__cell s-compare-page__cell--header-actions" data-compare-col="${index}" data-handle="${escapeHtml(entry.product.handle)}">
          ${renderProductActionsCell(entry, labels)}
        </div>
      `;
    })
    .join("");

  return (
    titleCell +
    imageCells +
    renderHeaderLabelSpacer() +
    titleCells +
    renderHeaderLabelSpacer() +
    priceCells +
    renderHeaderLabelSpacer() +
    actionCells
  );
}

async function addToCart(variantId) {
  if (!variantId) {
    return false;
  }

  const formData = new FormData();
  formData.append("id", variantId);
  formData.append("quantity", "1");

  const response = await fetch(toRootPath("/cart/add.js"), {
    method: "POST",
    body: formData,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest"
    }
  });

  if (!response.ok) {
    return false;
  }

  document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  return true;
}

class SnCompareStickyBar extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleStoreChange = this.handleStoreChange.bind(this);
    this.syncHeaderScrollState = this.syncHeaderScrollState.bind(this);
  }

  connectedCallback() {
    this.slots = Array.from(this.querySelectorAll("[data-compare-sticky-slot]"));
    this._lastScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    this._scrollDirection = "down";
    this.addEventListener("click", this.handleClick);
    document.addEventListener(CHANGE_EVENT, this.handleStoreChange);
    this.render();
    this.initHeaderScrollSync();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    document.removeEventListener(CHANGE_EVENT, this.handleStoreChange);
    this.teardownHeaderScrollSync();
  }

  initHeaderScrollSync() {
    if (!this.classList.contains("is-docked")) return;

    this.headerSection = document.querySelector(".hdt-section-header--main");
    if (!this.headerSection) return;

    this.headerObserver = new MutationObserver(this.syncHeaderScrollState);
    this.headerObserver.observe(this.headerSection, {
      attributes: true,
      attributeFilter: ["class"]
    });
    window.addEventListener("scroll", this.syncHeaderScrollState, { passive: true });
    this.syncHeaderScrollState();
  }

  teardownHeaderScrollSync() {
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    window.removeEventListener("scroll", this.syncHeaderScrollState);
  }

  /** Visible at top and on scroll-down; hidden only when scrolling up away from top. */
  syncHeaderScrollState() {
    const headerSection = this.headerSection;
    if (!headerSection) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (scrollTop > this._lastScrollTop + 1) {
      this._scrollDirection = "down";
    } else if (scrollTop < this._lastScrollTop - 1) {
      this._scrollDirection = "up";
    }
    this._lastScrollTop = scrollTop;

    const isAtTop = scrollTop <= 1;
    const scrolledPast = headerSection.classList.contains("scrolled-past-header");
    const headerHidden = headerSection.classList.contains("shopify-section-header-hidden");
    const shouldHideForHeader = !isAtTop && scrolledPast && this._scrollDirection === "up" && !headerHidden;

    this.classList.toggle("is-scroll-context", scrolledPast);
    this.classList.toggle("is-header-hidden", headerHidden);
    this.classList.toggle("is-bar-visible", !shouldHideForHeader);
  }

  get shopAllUrl() {
    const fallbackUrl = this.dataset.shopAllUrl || toRootPath("/collections/all");
    return resolveCompareCollectionReturnUrl(fallbackUrl);
  }

  get removeLabelTemplate() {
    return this.dataset.removeLabel || "Remove [title] from comparison";
  }

  get compareBase() {
    return this.dataset.compareBase || this._compareBase || "/pages/compare";
  }

  handleClick(event) {
    const removeButton = event.target.closest("[data-compare-sticky-remove]");
    if (removeButton) {
      event.preventDefault();
      const slot = removeButton.closest("[data-compare-sticky-slot]");
      const handle = slot?.dataset.handle;
      if (handle) {
        snCompareStore.remove(handle);
        syncUrlFromStore(this.compareBase);
      }
    }
  }

  handleStoreChange() {
    this.render();
  }

  set compareBase(path) {
    this._compareBase = path;
  }

  render() {
    const items = Array.from({ length: MAX_ITEMS }, (_, index) => snCompareStore.getItems()[index]);
    const slots = this.slots || [];

    slots.forEach((slot, index) => {
      const item = items[index];
      const addLink = slot.querySelector("[data-compare-sticky-add]");
      const filled = slot.querySelector("[data-compare-sticky-filled]");
      const image = slot.querySelector("[data-compare-sticky-image]");
      const title = slot.querySelector("[data-compare-sticky-title]");
      const removeButton = slot.querySelector("[data-compare-sticky-remove]");

      if (addLink) {
        addLink.href = this.shopAllUrl;
      }

      if (!item) {
        slot.classList.add("is-empty");
        slot.classList.remove("is-filled");
        slot.removeAttribute("data-handle");
        addLink?.removeAttribute("hidden");
        filled?.setAttribute("hidden", "");
        return;
      }

      slot.classList.remove("is-empty");
      slot.classList.add("is-filled");
      slot.dataset.handle = item.handle;
      addLink?.setAttribute("hidden", "");
      filled?.removeAttribute("hidden");

      if (image) {
        image.src = item.image || "";
        image.alt = item.title || "";
      }
      if (title) {
        title.textContent = item.title || item.handle;
      }
      if (removeButton) {
        removeButton.setAttribute(
          "aria-label",
          this.removeLabelTemplate.replace("[title]", item.title || item.handle)
        );
      }
    });

    this.hidden = !items.some(Boolean);
  }
}

class SnComparePage extends HTMLElement {
  constructor() {
    super();
    this.handleStoreChange = this.handleStoreChange.bind(this);
    this.handleGridClick = this.handleGridClick.bind(this);
    this.handleGridChange = this.handleGridChange.bind(this);
    this.updateMobileEndScrollGap = this.updateMobileEndScrollGap.bind(this);
    this.entries = [];
  }

  connectedCallback() {
    this.statusNode = this.querySelector("[data-compare-page-status]");
    this.emptyNode = this.querySelector("[data-compare-page-empty]");
    this.contentNode = this.querySelector("[data-compare-page-content]");
    this.gridNode = this.querySelector("[data-compare-page-grid]");
    this.sectionNode = this.closest(".s-compare-page-section");
    this.stickyBar = document.querySelector("sn-compare-sticky-bar");

    if (this.stickyBar) {
      this.stickyBar.compareBase = this.compareBase;
      this.stickyBar.dataset.compareBase = this.compareBase;
      this.stickyBar.dataset.shopAllUrl = this.shopAllUrl;
      this.stickyBar.dataset.removeLabel = this.dataset.removeLabel || "";
    }

    this.gridNode?.addEventListener("click", this.handleGridClick);
    this.gridNode?.addEventListener("change", this.handleGridChange);
    document.addEventListener(CHANGE_EVENT, this.handleStoreChange);
    this._mobileMq = window.matchMedia(MOBILE_COMPARE_MQ);
    this._onMobileMqChange = () => {
      if (this.gridNode?.querySelector("[data-compare-skeleton]")) {
        this.renderSkeleton();
        return;
      }
      if (this.entries.length > 0) {
        this.render();
      }
    };
    this._mobileMq.addEventListener("change", this._onMobileMqChange);
    window.addEventListener("resize", this.updateMobileEndScrollGap, { passive: true });

    if (parseProductsFromUrl().length > 0) {
      this.renderSkeleton();
    }

    this.init();
  }

  disconnectedCallback() {
    this.gridNode?.removeEventListener("click", this.handleGridClick);
    this.gridNode?.removeEventListener("change", this.handleGridChange);
    document.removeEventListener(CHANGE_EVENT, this.handleStoreChange);
    this._mobileMq?.removeEventListener("change", this._onMobileMqChange);
    window.removeEventListener("resize", this.updateMobileEndScrollGap);
  }

  get compareBase() {
    return this.dataset.compareBase || "/pages/compare";
  }

  get shopAllUrl() {
    const fallbackUrl = this.dataset.shopAllUrl || toRootPath("/collections/all");
    return resolveCompareCollectionReturnUrl(fallbackUrl);
  }

  get specRowOrder() {
    return this.dataset.specRowOrder || "";
  }

  get warrantyLabel() {
    return this.dataset.warrantyLabel || "Warranty";
  }

  get messages() {
    return {
      empty: this.dataset.emptyMessage || "Select products to compare.",
      loading: this.dataset.loadingMessage || "Loading selected products...",
      error: this.dataset.errorMessage || "Unable to load products."
    };
  }

  get labels() {
    return {
      addToCart: this.dataset.addToCartLabel || "Add to cart",
      soldOut: this.dataset.soldOutLabel || "Sold out",
      learnMore: this.dataset.learnMoreLabel || "Learn more",
      addProduct: this.dataset.addProductLabel || "Add a product",
      removeTemplate: this.dataset.removeLabel || "Remove [title] from comparison"
    };
  }

  async init() {
    const urlProducts = parseProductsFromUrl();
    const urlHandles = urlProducts.map((product) => product.handle);
    const persistedSelections = getPersistedCompareSelections();
    const variantIdsByHandle = getCompareVariantIdsByHandle();

    if (urlHandles.length === 0) {
      snCompareStore.clear();
      this.showEmpty();
      return;
    }

    this.setLoading(true);

    const products = await this.fetchProductsInOrder(urlHandles);

    if (products.length === 0) {
      this.showError();
      return;
    }

    const specsList = await Promise.all(
      products.map(async (product) => {
        const variantId = getVariantIdForHandle(product.handle, variantIdsByHandle);
        const data = await fetchProductSpecs(product.handle, variantId);
        return {
          product,
          specs: { specifications: data.specifications, warranty: data.warranty },
          collections: data.collections,
          swatchHtml: data.swatchHtml
        };
      })
    );

    const resolvedEntries = resolveComparePageEntries(specsList);

    if (resolvedEntries.length === 0) {
      snCompareStore.clear();
      this.showEmpty();
      syncUrlFromStore(this.compareBase, { replace: true });
      return;
    }

    snCompareStore.replaceItemsFromProducts(
      resolvedEntries.map((entry) => ({
        ...entry.product,
        collections: entry.collections
      })),
      { variantIdsByHandle, emit: false }
    );

    this.entries = attachVariantIdsToEntries(
      resolvedEntries,
      variantIdsByHandle,
      persistedSelections
    );

    compareDebug("init:entries", this.entries.map((entry) => ({
      handle: entry?.product?.handle,
      variantId: entry?.variantId,
      compareImage: entry?.compareImage
    })));

    console.log("[sn-compare] init:variant-map", variantIdsByHandle);
    console.log("[sn-compare] init:store-items", snCompareStore.getItems());

    syncUrlFromStore(this.compareBase, { replace: true });
    this.render();
  }

  handleStoreChange(event) {
    const action = event?.detail?.action;
    if (action === "sync" || action === "update") {
      return;
    }
    this.reloadFromStore();
  }

  async reloadFromStore() {
    const handles = snCompareStore.getHandles();
    if (handles.length === 0) {
      this.showEmpty();
      syncUrlFromStore(this.compareBase);
      return;
    }

    this.setLoading(true);
    const persistedSelections = getPersistedCompareSelections();
    const variantIdsByHandle = getCompareVariantIdsByHandle();
    const products = await this.fetchProductsInOrder(handles);
    const specsList = await Promise.all(
      products.map(async (product) => {
        const variantId = getVariantIdForHandle(product.handle, variantIdsByHandle);
        const data = await fetchProductSpecs(product.handle, variantId);
        return {
          product,
          specs: { specifications: data.specifications, warranty: data.warranty },
          collections: data.collections,
          swatchHtml: data.swatchHtml
        };
      })
    );

    const resolvedEntries = resolveComparePageEntries(specsList);

    if (resolvedEntries.length === 0) {
      snCompareStore.clear();
      this.showEmpty();
      syncUrlFromStore(this.compareBase);
      return;
    }

    snCompareStore.replaceItemsFromProducts(
      resolvedEntries.map((entry) => ({
        ...entry.product,
        collections: entry.collections
      })),
      { emit: false }
    );

    this.entries = attachVariantIdsToEntries(
      resolvedEntries,
      variantIdsByHandle,
      persistedSelections
    );
    this.render();
    syncUrlFromStore(this.compareBase);
  }

  async fetchProductsInOrder(handles) {
    const fetched = await Promise.all(handles.map((handle) => fetchProduct(handle)));
    const productMap = new Map(fetched.filter(Boolean).map((product) => [sanitizeHandle(product.handle), product]));
    return handles.map((handle) => productMap.get(sanitizeHandle(handle))).filter(Boolean);
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.renderSkeleton();
      return;
    }

    this.clearLoadingState();
  }

  clearLoadingState() {
    if (this.statusNode) {
      this.statusNode.hidden = true;
    }
    this.classList.remove("is-loading");
    this.gridNode?.classList.remove("is-loading");
    this.gridNode?.removeAttribute("aria-busy");
    this.gridNode?.removeAttribute("aria-label");
    this.contentNode?.removeAttribute("data-loading");
  }

  setSectionEmptyState(isEmpty) {
    this.sectionNode?.classList.toggle("is-empty-state", Boolean(isEmpty));
  }

  renderSkeleton() {
    if (!this.gridNode) {
      return;
    }
    this.setSectionEmptyState(false);

    const pageTitle =
      this.querySelector(".s-compare-page__heading")?.textContent?.trim() || "Compare";

    if (this.statusNode) {
      this.statusNode.hidden = true;
      this.statusNode.textContent = this.messages.loading;
    }

    this.classList.add("is-loading");
    this.emptyNode?.setAttribute("hidden", "");
    this.contentNode?.removeAttribute("hidden");
    this.contentNode?.setAttribute("data-loading", "");
    this.gridNode.classList.add("is-loading");
    this.gridNode.setAttribute("aria-busy", "true");
    this.gridNode.setAttribute("aria-label", this.messages.loading);
    this.gridNode.innerHTML = renderSkeletonTable(pageTitle, this.labels, this.specRowOrder);
  }

  showEmpty() {
    this.clearLoadingState();
    this.setSectionEmptyState(true);
    this.classList.add("is-empty-state");
    if (this.statusNode) {
      this.statusNode.hidden = true;
    }
    this.emptyNode?.removeAttribute("hidden");
    this.contentNode?.setAttribute("hidden", "");
    this.stickyBar?.render();
    if (this.gridNode) {
      this.gridNode.innerHTML = "";
      this.gridNode.classList.remove("is-loading");
    }
  }

  showError() {
    this.clearLoadingState();
    this.setSectionEmptyState(false);
    this.classList.add("is-empty-state");
    if (this.statusNode) {
      this.statusNode.hidden = false;
      this.statusNode.textContent = this.messages.error;
    }
    this.emptyNode?.setAttribute("hidden", "");
    this.contentNode?.setAttribute("hidden", "");
    if (this.gridNode) {
      this.gridNode.innerHTML = "";
      this.gridNode.classList.remove("is-loading");
    }
  }

  handleGridClick(event) {
    const removeButton = event.target.closest("[data-compare-remove]");
    if (removeButton) {
      event.preventDefault();
      const handle = removeButton.dataset.handle;
      if (handle) {
        snCompareStore.remove(handle);
        syncUrlFromStore(this.compareBase);
      }
      return;
    }

    const addButton = event.target.closest("[data-compare-add-to-cart]");
    if (addButton) {
      event.preventDefault();
      if (addButton.disabled || addButton.getAttribute("aria-busy") === "true") {
        return;
      }

      const columnCell = addButton.closest("[data-compare-col]");
      const handle = columnCell?.dataset.handle;
      const entry = handle
        ? this.entries.find(
            (item) => item?.product && sanitizeHandle(item.product.handle) === sanitizeHandle(handle)
          )
        : null;
      const variantId =
        getEntryCompareDisplay(entry)?.variant?.id || addButton.dataset.variantId;

      if (!variantId) {
        return;
      }

      setCompareAtcLoading(addButton, true);
      addToCart(variantId).finally(() => {
        setCompareAtcLoading(addButton, false);
      });
    }
  }

  handleGridChange(event) {
    const radio = event.target;
    if (radio?.type !== "radio" || !radio.checked || !radio.closest(".s-compare-page__price-swatch")) {
      return;
    }

    const columnCell = radio.closest("[data-compare-col]");
    const handle =
      columnCell?.dataset.handle ||
      radio.closest("[data-handle]")?.dataset.handle ||
      radio.closest("[data-compare-swatches]")?.closest("[data-handle]")?.dataset.handle;

    if (!handle) {
      return;
    }

    const entry = this.entries.find(
      (item) => item?.product && sanitizeHandle(item.product.handle) === sanitizeHandle(handle)
    );
    if (!entry) {
      return;
    }

    const { url } = radio.dataset;
    const vid = resolveVariantIdFromSwatchRadio(radio, entry.product);
    if (!vid) {
      return;
    }

    if (url) {
      try {
        const parsed = new URL(url, window.location.origin);
        if (!parsed.pathname.includes(`/products/${sanitizeHandle(handle)}`)) {
          return;
        }
      } catch (error) {
        return;
      }
    }

    entry.variantId = vid;
    entry.compareImage = resolveVariantImageFromSwatchRadio(radio, entry.product, vid);
    entry.compareUrl = url || appendVariantParam(entry.product.url.split("?")[0], vid);

    const storeItem = snCompareStore.getItems().find((item) => item.handle === sanitizeHandle(handle));
    updateCompareStoreItem(handle, {
      handle: sanitizeHandle(handle),
      id: String(entry.product.id || storeItem?.id || ""),
      variantId: vid,
      title: entry.product.title,
      image: entry.compareImage,
      url: entry.compareUrl,
      collections: entry.collections || storeItem?.collections || []
    });

    syncUrlFromStore(this.compareBase);
    this.refreshCompareColumn(handle);
  }

  getEntryColumnIndex(handle) {
    return this.entries.findIndex(
      (entry) => entry?.product && sanitizeHandle(entry.product.handle) === sanitizeHandle(handle)
    );
  }

  refreshCompareColumn(handle) {
    const colIndex = this.getEntryColumnIndex(handle);
    if (colIndex === -1 || !this.gridNode) {
      return;
    }

    const entry = this.entries[colIndex];
    const display = getEntryCompareDisplay(entry);
    const labels = this.labels;

    this.gridNode.querySelectorAll(`[data-compare-col="${colIndex}"]`).forEach((cell) => {
      if (
        cell.classList.contains("s-compare-page__cell--header-image") ||
        cell.classList.contains("s-compare-page__mobile-cell--image")
      ) {
        const image = cell.querySelector(".s-compare-page__product-image");
        const link = cell.querySelector(".s-compare-page__product-image-link");
        const imageUrl = entry.compareImage || display.image;
        if (image && imageUrl) {
          image.src = imageUrl;
        }
        if (link) {
          link.href = display.url;
        }
        return;
      }

      if (
        cell.classList.contains("s-compare-page__cell--header-title") ||
        cell.classList.contains("s-compare-page__mobile-cell--title")
      ) {
        const titleLink = cell.querySelector(".s-compare-page__product-title");
        if (titleLink) {
          titleLink.href = display.url;
        }
        return;
      }

      if (
        cell.classList.contains("s-compare-page__cell--header-price") ||
        cell.classList.contains("s-compare-page__mobile-cell--price")
      ) {
        cell.innerHTML = renderProductPriceCell(entry);
        return;
      }

      if (
        cell.classList.contains("s-compare-page__cell--header-actions") ||
        cell.classList.contains("s-compare-page__mobile-cell--actions")
      ) {
        cell.innerHTML = renderProductActionsCell(entry, labels);
        return;
      }

      if (
        cell.classList.contains("s-compare-page__cell--footer") ||
        cell.classList.contains("s-compare-page__mobile-cell--footer")
      ) {
        cell.innerHTML = renderAtcButton(display.variant, labels, "s-compare-page__btn--block");
      }
    });

    syncCompareSwatchSelection(this.gridNode, entry, colIndex);

    this.stickyBar?.render();
    this.updateMobileEndScrollGap();
  }

  updateMobileEndScrollGap() {
    if (!this.gridNode) {
      return;
    }

    const scrollNode = this.gridNode.querySelector(".s-compare-page__mobile-scroll");
    if (!scrollNode) {
      return;
    }

    scrollNode.style.setProperty("--compare-mobile-end-gap", "0px");
    if (!isMobileCompareLayout()) {
      return;
    }

    const lastColumnCell = scrollNode.querySelector(`.s-compare-page__mobile-cell--image[data-compare-col="${MAX_ITEMS - 1}"]`);
    if (!lastColumnCell) {
      return;
    }

    const currentMaxScroll = scrollNode.scrollWidth - scrollNode.clientWidth;
    if (currentMaxScroll <= 0) {
      return;
    }

    const scrollRect = scrollNode.getBoundingClientRect();
    const cellRect = lastColumnCell.getBoundingClientRect();
    const paddingLeft = parseFloat(window.getComputedStyle(scrollNode).paddingLeft) || 0;
    const cellContentStart = cellRect.left - scrollRect.left + scrollNode.scrollLeft;
    const targetMaxScroll = Math.max(0, cellContentStart - paddingLeft);
    const extraGap = Math.max(0, Math.ceil(targetMaxScroll - currentMaxScroll));

    if (extraGap > 0) {
      scrollNode.style.setProperty("--compare-mobile-end-gap", `${extraGap}px`);
    }
  }

  render() {
    if (this.entries.length === 0) {
      this.showEmpty();
      return;
    }

    this.clearLoadingState();
    this.setSectionEmptyState(false);
    this.classList.remove("is-empty-state");
    if (this.statusNode) {
      this.statusNode.hidden = true;
    }
    this.emptyNode?.setAttribute("hidden", "");
    this.contentNode?.removeAttribute("hidden");

    const storeItems = Array.from({ length: MAX_ITEMS }, (_, index) => {
      return snCompareStore.getItems()[index] || null;
    });

    this.stickyBar?.render();
    this.renderGrid(storeItems);
  }

  renderGrid(storeItems) {
    if (!this.gridNode) {
      return;
    }

    const paddedEntries = Array.from({ length: MAX_ITEMS }, (_, index) => this.entries[index] || null);

    const slots = Array.from({ length: MAX_ITEMS }, (_, index) => ({
      entry: paddedEntries[index],
      item: storeItems[index],
      index
    }));

    const specRows = buildSpecRows(
      paddedEntries.filter(Boolean),
      this.specRowOrder,
      this.warrantyLabel
    );
    const labels = this.labels;

    const pageTitle =
      this.querySelector(".s-compare-page__heading")?.textContent?.trim() || "Compare";

    if (isMobileCompareLayout()) {
      this.gridNode.innerHTML = renderMobileGrid(
        slots,
        specRows,
        labels,
        this.closeIcon,
        this.shopAllUrl,
        this.warrantyLabel
      );
    } else {
      const headerCells = renderHeaderRows(
        slots,
        pageTitle,
        labels,
        this.closeIcon,
        this.shopAllUrl,
        this.plusIcon
      );

      const specCells = specRows.flatMap((rowKey) => {
        const labelCell = `
        <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--spec">
          ${renderRule()}
          <span class="s-compare-page__spec-label ${COMPARE_TYPO.specLabel}">${escapeHtml(rowKey)}</span>
        </div>
      `;

        const valueCells = slots
          .map(({ entry, index }) => {
            const value = entry ? getSpecValue(entry, rowKey, this.warrantyLabel) : "-";
            return `
            <div class="s-compare-page__cell s-compare-page__cell--spec${entry ? "" : " is-empty"}" data-compare-col="${index}">
              ${renderRule()}
              <div class="s-compare-page__spec-value ${COMPARE_TYPO.specValue}">${formatSpecValueHtml(value)}</div>
            </div>
          `;
          })
          .join("");

        return labelCell + valueCells;
      });

      const footerCells = slots
        .map(({ entry, index }) => {
          if (!entry?.product) {
            return `<div class="s-compare-page__cell s-compare-page__cell--footer is-empty" data-compare-col="${index}"></div>`;
          }
          const variant = getEntryCompareDisplay(entry).variant;
          return `
          <div class="s-compare-page__cell s-compare-page__cell--footer" data-compare-col="${index}">
            ${renderAtcButton(variant, labels, "s-compare-page__btn--block")}
          </div>
        `;
        })
        .join("");

      this.gridNode.innerHTML = `
      <div class="s-compare-page__table">
        ${headerCells}
        ${specCells.join("")}
        <div class="s-compare-page__cell s-compare-page__cell--label s-compare-page__cell--footer-spacer" aria-hidden="true"></div>
        ${footerCells}
      </div>
    `;
    }

    syncCompareSwatchSelections(this.gridNode, paddedEntries);
    this.updateMobileEndScrollGap();
  }

  get plusIcon() {
    if (!this._plusIcon) {
      const template = document.getElementById("sn-compare-icon-plus");
      this._plusIcon = template?.innerHTML?.trim() || "+";
    }
    return this._plusIcon;
  }

  get closeIcon() {
    if (!this._closeIcon) {
      const template = document.getElementById("sn-compare-icon-close");
      this._closeIcon = template?.innerHTML?.trim() || "×";
    }
    return this._closeIcon;
  }
}

if (!customElements.get("sn-compare-sticky-bar")) {
  customElements.define("sn-compare-sticky-bar", SnCompareStickyBar);
}

if (!customElements.get("sn-compare-page")) {
  customElements.define("sn-compare-page", SnComparePage);
}
